import type {
  CreateInvoiceRequest,
  CreateInvoiceLineItemRequest,
  Discount,
  Invoice,
  InvoiceLineItem,
  InvoicePaymentMethodId,
  TaxConfig,
} from '@repo/api-client/dtos';
import { z } from 'zod';
import { parseUserMoney } from '@/features/money';

/**
 * Invoice form schema for the web. Designed to round-trip cleanly
 * with the server's `CreateInvoiceRequest` / `UpdateInvoiceRequest`.
 *
 * Numeric inputs land as strings (the user types into a `<Input>`)
 * and we coerce + validate via Zod so wired values are always
 * normalised before they reach the wire decoder. Money amounts on
 * line items are user-input decimals; we lift them via
 * `parseUserMoney` then serialise to minor-units strings before the
 * mutation fires.
 */
/**
 * UI-friendly discount shape. Mirrors the wire's `Discount` union
 * (percentage / fixed) but keeps the amount as a user-typed decimal
 * string so the input cell echoes exactly what the user pressed —
 * `parseUserMoney` is the gate that turns it into wire minor units at
 * submit / live-total time. Percentages are also strings so "12.5" and
 * "12,5" both flow through unmodified until the dialog parses them.
 */
export const formDiscountSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('percentage'), rate: z.string().trim().min(1) }),
  z.object({ type: z.literal('fixed'), amount: z.string().trim().min(1) }),
]);

export type FormDiscount = z.infer<typeof formDiscountSchema>;

export const invoicePaymentMethodSchema = z.object({
  id: z.enum(['none', 'paypal', 'stripe-checkout', 'bank-transfer', 'cash', 'check', 'custom']),
  label: z.string().min(1),
  detail: z.string().optional(),
});

export const invoiceLineItemFormSchema = z.object({
  id: z.string().optional(),
  description: z.string().trim().min(1, 'Description is required'),
  quantity: z
    .string()
    .trim()
    .min(1, 'Required')
    .refine((v) => Number.isFinite(Number(v)) && Number(v) > 0, 'Must be > 0'),
  unitPriceInput: z
    .string()
    .trim()
    .min(1, 'Required'),
  unit: z.string().min(1).default('fixed'),
  taxable: z.boolean(),
  /**
   * Per-line discount (set via the row's discount dialog). `undefined`
   * = no per-line discount.
   */
  discount: formDiscountSchema.optional(),
  /**
   * Per-line tax rate (percentage as a decimal string, e.g. "8.875").
   * Only consulted when the invoice-level tax method is `per_item`.
   * `undefined` = "no rate set" → domain treats as zero for that line.
   */
  taxRate: z.string().trim().optional(),
  notes: z.string().trim().optional().or(z.literal('')),
});

export type InvoiceLineItemFormValues = z.infer<typeof invoiceLineItemFormSchema>;

export const taxConfigFormSchema = z.discriminatedUnion('method', [
  z.object({ method: z.literal('none') }),
  z.object({
    method: z.literal('on_total'),
    rate: z.string().trim().min(1, 'Required'),
    label: z.string().optional(),
    inclusive: z.boolean(),
  }),
  z.object({
    method: z.literal('per_item'),
    label: z.string().optional(),
    inclusive: z.boolean(),
  }),
  z.object({
    method: z.literal('as_deduction'),
    rate: z.string().trim().min(1, 'Required'),
    label: z.string().optional(),
  }),
]);

export type TaxConfigFormValues = z.infer<typeof taxConfigFormSchema>;

export const invoiceFormSchema = z.object({
  clientId: z.string().min(1, 'Pick a client'),
  currency: z.string().min(1).max(3),
  issueDate: z.string().min(1, 'Required'),
  dueDate: z.string().min(1, 'Required'),
  templateId: z.string().min(1).default('classic'),
  notes: z.string().trim().max(4000).optional().or(z.literal('')),
  terms: z.string().trim().max(4000).optional().or(z.literal('')),
  signature: z.string().trim().max(120).optional().or(z.literal('')),
  tax: taxConfigFormSchema,
  /** Invoice-level discount (set via the totals sidebar dialog). */
  invoiceDiscount: formDiscountSchema.optional(),
  /** Selected payment methods, in user-chosen order. */
  paymentMethods: z.array(invoicePaymentMethodSchema).default([]),
  lineItems: z.array(invoiceLineItemFormSchema).min(1, 'Add at least one line item'),
});

export type InvoiceFormValues = z.infer<typeof invoiceFormSchema>;

export function emptyLineItem(): InvoiceLineItemFormValues {
  return {
    description: '',
    quantity: '1',
    unitPriceInput: '',
    unit: 'item',
    taxable: true,
    discount: undefined,
    taxRate: undefined,
    notes: '',
  };
}

export function invoiceFormDefaults(currency: string): InvoiceFormValues {
  const today = new Date();
  const due = new Date(today);
  due.setDate(due.getDate() + 14);
  return {
    clientId: '',
    currency,
    issueDate: toDateInputValue(today),
    dueDate: toDateInputValue(due),
    templateId: 'classic',
    // Friendly closing line by default — sits at the bottom of the
    // invoice. Sellers can edit or clear it; clearing means no notes
    // block renders (templates gate on `invoice.notes` being truthy).
    notes: 'Thank you for your business.',
    terms: '',
    signature: '',
    tax: { method: 'none' },
    invoiceDiscount: undefined,
    paymentMethods: [],
    // Empty list — surfaces the empty-state CTA. Schema validation
    // (`.min(1)`) fires on submit if the user never adds one.
    lineItems: [],
  };
}

/**
 * Default label for a payment method id. Mirrors the catalogue mobile
 * uses; the label is editable downstream (the wire allows arbitrary
 * strings) but having a sane default keeps the dialog one-tap.
 */
export function defaultPaymentMethodLabel(id: InvoicePaymentMethodId): string {
  switch (id) {
    case 'none':
      return 'None';
    case 'paypal':
      return 'PayPal';
    case 'stripe-checkout':
      return 'Accept card payment online';
    case 'bank-transfer':
      return 'Bank transfer';
    case 'cash':
      return 'Cash';
    case 'check':
      return 'Check';
    case 'custom':
      return 'Other';
  }
}

export function toDateInputValue(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Convert a Money lifted from user input into a wire-format minor-units
 * string. The `Money.amount` is already a bigint of minor units, so
 * this is just `.toString()`. Centralised so the line-item builder
 * has a single name to call.
 */
function moneyToMinorString(amount: bigint): string {
  return amount.toString();
}

/**
 * Convert the form's UI-friendly discount shape into the wire's
 * discriminated union. For fixed amounts this parses the user-typed
 * decimal via `parseUserMoney` (same gate the wire uses) — invalid
 * input collapses to `undefined`, which means "no discount" rather
 * than a silent zero. Callers can detect the failure by comparing the
 * returned value to `undefined`.
 */
export function formDiscountToWire(
  d: FormDiscount | undefined,
  currency: string,
): Discount | undefined {
  if (!d) return undefined;
  if (d.type === 'percentage') {
    if (!/^[0-9]+(?:[.,][0-9]+)?$/.test(d.rate.trim())) return undefined;
    return { type: 'percentage', rate: d.rate.replace(',', '.') };
  }
  const parsed = parseUserMoney(d.amount, currency);
  if (!parsed.ok) return undefined;
  return { type: 'fixed', amount: parsed.value.amount.toString() };
}

/**
 * Inverse of {@link formDiscountToWire} — lift a stored discount back
 * into the UI shape. Used by the edit screen so the dialog re-opens
 * with whatever the user previously set.
 */
export function wireDiscountToForm(
  d: Discount | null | undefined,
  currency: string,
): FormDiscount | undefined {
  if (!d) return undefined;
  if (d.type === 'percentage') return { type: 'percentage', rate: String(d.rate) };
  return { type: 'fixed', amount: minorToDecimal(String(d.amount), currency) };
}

/**
 * Lift a stored line item's discount columns into the UI shape.
 * Stored line rows split the union into `discount` + `discountType`
 * columns (legacy shape); recombine into the discriminated union and
 * then into the form's display shape via {@link wireDiscountToForm}.
 */
function lineItemDiscountFromWire(
  line: InvoiceLineItem,
  currency: string,
): FormDiscount | undefined {
  if (line.discount === null || line.discount === undefined) return undefined;
  if (line.discountType === 'percentage') {
    return { type: 'percentage', rate: String(line.discount) };
  }
  if (line.discountType === 'fixed') {
    return { type: 'fixed', amount: minorToDecimal(String(line.discount), currency) };
  }
  return undefined;
}

/**
 * Translate a parsed form payload into the server's
 * `CreateInvoiceRequest` shape. Line unit prices arrive pre-resolved
 * (caller has already run `parseUserMoney`); per-line discounts and
 * the invoice-level discount are converted here.
 */
export function buildCreateInvoiceRequest(args: {
  values: InvoiceFormValues;
  lineUnitPriceMinor: string[];
}): CreateInvoiceRequest {
  const tax: TaxConfig = formTaxToWire(args.values.tax);
  const currency = args.values.currency;
  return {
    clientId: args.values.clientId,
    status: 'draft',
    currency,
    issueDate: new Date(args.values.issueDate).toISOString(),
    dueDate: new Date(args.values.dueDate).toISOString(),
    templateId: args.values.templateId,
    notes: args.values.notes || undefined,
    terms: args.values.terms || undefined,
    signature: args.values.signature || undefined,
    tax,
    invoiceDiscount: formDiscountToWire(args.values.invoiceDiscount, currency),
    paymentMethods: args.values.paymentMethods ?? [],
    lineItems: args.values.lineItems.map((line, idx): CreateInvoiceLineItemRequest => {
      const lineDiscount = formDiscountToWire(line.discount, currency);
      const taxRate =
        line.taxRate && line.taxRate.trim() !== '' ? line.taxRate.trim() : undefined;
      return {
        description: line.description,
        quantity: line.quantity,
        unitPrice: args.lineUnitPriceMinor[idx]!,
        unit: line.unit,
        taxable: line.taxable,
        notes: line.notes || undefined,
        ...(lineDiscount ? { discount: lineDiscount } : {}),
        ...(taxRate ? { taxRate } : {}),
      };
    }),
  };
}

export function formTaxToWire(tax: TaxConfigFormValues): TaxConfig {
  switch (tax.method) {
    case 'none':
      return { method: 'none' };
    case 'on_total':
      return {
        method: 'on_total',
        rate: tax.rate,
        inclusive: tax.inclusive,
        label: tax.label,
      };
    case 'per_item':
      return {
        method: 'per_item',
        inclusive: tax.inclusive,
        label: tax.label,
      };
    case 'as_deduction':
      return { method: 'as_deduction', rate: tax.rate, label: tax.label };
  }
}

/**
 * Lift an Invoice (from the wire) into editable form values. Used by
 * the edit screen so the user can keep working in the same shape.
 */
export function invoiceFormFromWire(invoice: Invoice): InvoiceFormValues {
  const tax: TaxConfigFormValues =
    invoice.taxConfig.method === 'none'
      ? { method: 'none' }
      : invoice.taxConfig.method === 'on_total'
        ? {
            method: 'on_total',
            rate: String(invoice.taxConfig.rate),
            label: invoice.taxConfig.label,
            inclusive: invoice.taxConfig.inclusive ?? false,
          }
        : invoice.taxConfig.method === 'per_item'
          ? {
              method: 'per_item',
              label: invoice.taxConfig.label,
              inclusive: invoice.taxConfig.inclusive ?? false,
            }
          : {
              method: 'as_deduction',
              rate: String(invoice.taxConfig.rate),
              label: invoice.taxConfig.label,
            };
  const lineItems: InvoiceLineItemFormValues[] = (invoice.lineItems ?? []).map(
    (line: InvoiceLineItem) => ({
      id: line.id,
      description: line.description,
      quantity: String(line.quantity),
      unitPriceInput: minorToDecimal(line.unitPrice, invoice.currency),
      unit: line.unit,
      taxable: line.taxable,
      discount: lineItemDiscountFromWire(line, invoice.currency),
      taxRate:
        line.taxRate !== null && line.taxRate !== undefined
          ? String(line.taxRate)
          : undefined,
      notes: line.notes ?? '',
    }),
  );
  return {
    clientId: invoice.clientId,
    currency: invoice.currency,
    issueDate: invoice.issueDate.slice(0, 10),
    dueDate: invoice.dueDate.slice(0, 10),
    templateId: invoice.templateId ?? 'classic',
    notes: invoice.notes ?? '',
    terms: invoice.terms ?? '',
    signature: invoice.signature ?? '',
    tax,
    invoiceDiscount: wireDiscountToForm(invoice.invoiceDiscount, invoice.currency),
    paymentMethods: invoice.paymentMethods ?? [],
    lineItems: lineItems.length > 0 ? lineItems : [emptyLineItem()],
  };
}

import { getCurrencyScale } from '@repo/domain';

/**
 * Convert a wire minor-units string into a decimal display string for
 * the input field. Uses the currency's scale so JPY (scale 0) renders
 * `1500` as `"1500"`, USD (scale 2) renders `"100000"` as `"1000.00"`,
 * BHD (scale 3) renders `"1500"` as `"1.500"`.
 *
 * Pure string slicing — never goes through Number/parseFloat to avoid
 * the float-precision class of bug.
 */
export function minorToDecimal(minor: string, currency: string): string {
  const scale = getCurrencyScale(currency);
  if (scale === 0) return minor;
  const negative = minor.startsWith('-');
  const digits = negative ? minor.slice(1) : minor;
  const padded = digits.padStart(scale + 1, '0');
  const head = padded.slice(0, padded.length - scale);
  const tail = padded.slice(padded.length - scale);
  return `${negative ? '-' : ''}${head}.${tail}`;
}

export { moneyToMinorString };
